import { createFileRoute } from "@tanstack/react-router";
import { useState, useRef, lazy, Suspense, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/db";
import { Guard } from "@/components/Guard";
import { PageHeader } from "@/components/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Send, FileText, Plus, Trash2, Mail, AlertCircle, Download, Copy, ListChecks, Users, Sparkles, FilePen, RefreshCw, Eye, BookUser, Tags, Inbox, Pencil } from "lucide-react";
import { MailboxTab } from "@/components/MailboxTab";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";
import { useSignatures, applySignature, defaultSignature } from "@/lib/signatures";

const TiptapEditor = lazy(() =>
  import("@/components/TiptapEditor").then((m) => ({ default: m.TiptapEditor })),
);
const TiptapFallback = () => (
  <div className="border rounded-md min-h-[320px] animate-pulse bg-muted/30" />
);

export const Route = createFileRoute("/mailing")({ component: () => <Guard module="mailing"><MailingPage /></Guard> });

const INACTIVITY_YEARS = 3;

// Types partagés
interface MailingList {
  id: string;
  name: string;
  description: string | null;
  kind: "dynamic_active" | "dynamic_inactive" | "manual";
}

// Normalise une saison ("2025-2026" -> "2025") comme dans la page Adhérents
const normalizeSeason = (season?: string | null) =>
  (season ?? "").trim().match(/^(\d{4})(?:\s*-\s*\d{4})?$/)?.[1] ?? (season ?? "").trim();

/** Garantit un tableau : l'API PHP peut renvoyer un objet ou null selon l'endpoint. */
const asArray = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);


// Hook utilitaire : récupère la résolution d'une liste -> emails
function useMembersWithStatus() {
  return useQuery({
    queryKey: ["members-with-status"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("members")
        .select("*, memberships(season)")
        .order("last_name");
      if (error) throw error;
      const currentYear = new Date().getFullYear();
      const threshold = currentYear - INACTIVITY_YEARS;
      return asArray<any>(data).map((m: any) => {
        const seasons = asArray<any>(m.memberships)
          .map((x: any) => Number(normalizeSeason(x.season)))
          .filter((n: number) => Number.isFinite(n) && n > 0);

        // Repli : l'API /api/members renvoie last_season sans le tableau memberships[]
        const fallback = Number(normalizeSeason(m.last_season ?? null));
        if (Number.isFinite(fallback) && fallback > 0) seasons.push(fallback);
        const lastSeason = seasons.length > 0 ? Math.max(...seasons) : 0;
        const isInactive = !!m.archived || lastSeason < threshold;
        return { ...m, _lastSeason: lastSeason, _isInactive: isInactive };
      });
    },
  });
}


interface EmailCampaign {
  id: string;
  name: string;
  subject: string;
  body_html: string;
  recipient_emails: string[] | string | null;
  cc_emails?: string[] | string | null;
  bcc_emails?: string[] | string | null;
  is_mass_mailing: number | boolean;
  smtp_profile_id: string | null;
  status: "draft" | "sending" | "sent" | "failed";
  sent_at: string | null;
  last_error: string | null;
  created_at: string;
}

function normalizeRecipients(v: EmailCampaign["recipient_emails"]): string[] {
  if (Array.isArray(v)) return v;
  if (typeof v === "string") {
    try { const j = JSON.parse(v); return Array.isArray(j) ? j : []; } catch { return []; }
  }
  return [];
}

interface ComposePrefill {
  contactIds?: string[];
  emails?: string[];
}

function MailingPage() {
  const [activeTab, setActiveTab] = useState("inbox");
  const [draftToLoad, setDraftToLoad] = useState<EmailCampaign | null>(null);
  const [prefill, setPrefill] = useState<ComposePrefill | null>(null);

  const openDraft = (c: EmailCampaign) => {
    setDraftToLoad(c);
    setActiveTab("compose");
  };

  const composeTo = (p: ComposePrefill) => {
    setPrefill(p);
    setActiveTab("compose");
  };

  return (
    <>
      <PageHeader title="Mails" description="Envoyer des emails aux adhérents et gérer vos modèles & listes" />
      <Card className="p-4 mb-4 border-warning bg-warning/5">
        <div className="flex gap-3">
          <AlertCircle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
          <div className="text-sm">
            <strong>Configuration SMTP requise :</strong> les paramètres de votre serveur mail doivent être renseignés
            dans <em>Administration → Paramètres SMTP</em>. L'envoi effectif sera branché lors de la mise en production
            sur votre hébergeur (l'environnement d'aperçu Lovable Cloud ne peut pas se connecter à un SMTP classique).
          </div>
        </div>
      </Card>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="inbox"><Inbox className="h-4 w-4 mr-1.5" />Boîte de réception</TabsTrigger>
          <TabsTrigger value="compose"><Send className="h-4 w-4 mr-1.5" />Mailing de masse</TabsTrigger>
          <TabsTrigger value="drafts"><FilePen className="h-4 w-4 mr-1.5" />Brouillons</TabsTrigger>
          <TabsTrigger value="lists"><ListChecks className="h-4 w-4 mr-1.5" />Listes de diffusion</TabsTrigger>
          <TabsTrigger value="contacts"><BookUser className="h-4 w-4 mr-1.5" />Carnet d'adresses</TabsTrigger>
          <TabsTrigger value="templates"><FileText className="h-4 w-4 mr-1.5" />Modèles</TabsTrigger>
          <TabsTrigger value="signatures"><Pencil className="h-4 w-4 mr-1.5" />Signatures</TabsTrigger>
          <TabsTrigger value="history"><Mail className="h-4 w-4 mr-1.5" />Historique</TabsTrigger>
        </TabsList>
        <TabsContent value="inbox" className="mt-4"><MailboxTab /></TabsContent>
        <TabsContent value="compose" className="mt-4">
          <ComposeTab
            draft={draftToLoad}
            onDraftConsumed={() => setDraftToLoad(null)}
            prefill={prefill}
            onPrefillConsumed={() => setPrefill(null)}
          />
        </TabsContent>
        <TabsContent value="drafts" className="mt-4"><DraftsTab onEdit={openDraft} /></TabsContent>
        <TabsContent value="lists" className="mt-4"><ListsTab /></TabsContent>
        <TabsContent value="contacts" className="mt-4"><ContactsTab onCompose={composeTo} /></TabsContent>
        <TabsContent value="templates" className="mt-4"><TemplatesTab /></TabsContent>
        <TabsContent value="signatures" className="mt-4"><SignaturesTab /></TabsContent>
        <TabsContent value="history" className="mt-4"><HistoryTab /></TabsContent>
      </Tabs>
    </>
  );
}

function RichTextEditor(props: { value: string; onChange: (html: string) => void }) {
  return (
    <Suspense fallback={<TiptapFallback />}>
      <TiptapEditor {...props} />
    </Suspense>
  );
}

interface RecipientEntry {
  email: string;
  label: string;
  origin: string;
}

type FieldKey = "to" | "cc" | "bcc";

const FIELD_META: Record<FieldKey, { label: string; hint: string }> = {
  to: { label: "À", hint: "Destinataires principaux — visibles de tous." },
  cc: { label: "Cc", hint: "Copie — visibles de tous." },
  bcc: { label: "Cci", hint: "Copie cachée — masquée aux autres destinataires." },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const parseEmails = (v: string) =>
  Array.from(
    new Set(
      v
        .split(/[,;\s\n]+/)
        .map((s) => s.trim().toLowerCase())
        .filter((s) => EMAIL_RE.test(s)),
    ),
  );

function downloadCsv(entries: { label: string; email: string }[], baseName: string) {
  const csv = "Nom,Email\n" + entries.map((e) => `"${e.label.replace(/"/g, '""')}","${e.email}"`).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${baseName}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
  toast.success(`${entries.length} adresse(s) exportée(s)`);
}

interface FieldState {
  picked: RecipientEntry[];
  text: string;
}

const emptyField = (): FieldState => ({ picked: [], text: "" });

function RecipientField({
  fieldKey,
  state,
  onChange,
  onOpenPicker,
  emails,
}: {
  fieldKey: FieldKey;
  state: FieldState;
  onChange: (next: FieldState) => void;
  onOpenPicker: () => void;
  emails: string[];
}) {
  const meta = FIELD_META[fieldKey];
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label className="flex items-center gap-2">
          {meta.label}
          <Badge variant="secondary">{emails.length}</Badge>
        </Label>
        <Button type="button" variant="outline" size="sm" onClick={onOpenPicker}>
          <Plus className="h-4 w-4 mr-1.5" />Ajouter
        </Button>
      </div>
      {state.picked.length > 0 && (
        <div className="border rounded-md p-2 max-h-32 overflow-y-auto flex flex-wrap gap-1.5">
          {state.picked.map((e) => (
            <span key={e.email} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs">
              <span className="truncate max-w-[160px]" title={`${e.label} · ${e.email} · ${e.origin}`}>{e.label}</span>
              <button
                type="button"
                className="text-muted-foreground hover:text-destructive"
                onClick={() => onChange({ ...state, picked: state.picked.filter((p) => p.email !== e.email) })}
                aria-label={`Retirer ${e.email}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <Textarea
        value={state.text}
        onChange={(e) => onChange({ ...state, text: e.target.value })}
        placeholder="email1@exemple.com, email2@exemple.com"
        rows={2}
        className="text-sm"
      />
      <p className="text-xs text-muted-foreground">{meta.hint}</p>
    </div>
  );
}

function RecipientPicker({
  fieldKey,
  onClose,
  onAdd,
  members,
  contacts,
  contactCategories,
  lists,
  allListMembers,
  memberLabel,
  contactLabel,
}: {
  fieldKey: FieldKey | null;
  onClose: () => void;
  onAdd: (entries: RecipientEntry[]) => void;
  members: any[];
  contacts: Contact[];
  contactCategories: ContactCategory[];
  lists: MailingList[];
  allListMembers: { list_id: string; member_id: string | null; contact_id: string | null }[];
  memberLabel: (m: any) => string;
  contactLabel: (c: Contact) => string;
}) {
  const [search, setSearch] = useState("");
  const q = search.trim().toLowerCase();
  const match = (s: string) => !q || s.toLowerCase().includes(q);

  const activeMembers = members.filter((m: any) => m.email && !m._isInactive);
  const inactiveMembers = members.filter((m: any) => m.email && m._isInactive);

  const listEntries = (list: MailingList): RecipientEntry[] => {
    if (list.kind === "dynamic_active")
      return activeMembers.map((m: any) => ({ email: m.email, label: memberLabel(m), origin: list.name }));
    if (list.kind === "dynamic_inactive")
      return inactiveMembers.map((m: any) => ({ email: m.email, label: memberLabel(m), origin: list.name }));
    const rows = allListMembers.filter((r) => r.list_id === list.id);
    const mids = new Set(rows.map((r) => r.member_id).filter(Boolean) as string[]);
    const cids = new Set(rows.map((r) => r.contact_id).filter(Boolean) as string[]);
    return [
      ...members
        .filter((m: any) => m.email && mids.has(m.id))
        .map((m: any) => ({ email: m.email, label: memberLabel(m), origin: list.name })),
      ...contacts
        .filter((c) => c.email && cids.has(c.id))
        .map((c) => ({ email: c.email, label: contactLabel(c), origin: list.name })),
    ];
  };

  const visibleLists = useMemo(() => lists.filter((l) => match(l.name)), [lists, q]);
  const visibleCategories = useMemo(() => contactCategories.filter((c) => match(c.name)), [contactCategories, q]);

  const mailMembers = useMemo(
    () =>
      members
        .filter((m: any) => m.email)
        .filter((m: any) => match(`${m.first_name ?? ""} ${m.last_name ?? ""} ${m.email}`)),
    [members, q],
  );

  const mailContacts = useMemo(
    () =>
      contacts
        .filter((c) => c.email)
        .filter((c) =>
          match(`${c.first_name ?? ""} ${c.last_name ?? ""} ${c.organization ?? ""} ${c.job_title ?? ""} ${c.email}`),
        ),
    [contacts, q],
  );

  const push = (entries: RecipientEntry[]) => {
    const clean = entries.filter((e) => e.email && EMAIL_RE.test(e.email.toLowerCase()));
    if (clean.length === 0) return toast.error("Aucune adresse valide");
    onAdd(clean);
    toast.success(`${clean.length} destinataire${clean.length > 1 ? "s" : ""} ajouté${clean.length > 1 ? "s" : ""}`);
  };

  const groupRow = (key: string, label: string, count: number, build: () => RecipientEntry[]) => (
    <div key={key} className="flex items-center justify-between gap-2 py-1.5">
      <span className="text-sm truncate">
        {label} <span className="text-muted-foreground">({count})</span>
      </span>
      <Button type="button" variant="outline" size="sm" disabled={count === 0} onClick={() => push(build())}>
        Ajouter
      </Button>
    </div>
  );

  const sectionLabel = (text: string, count?: number) => (
    <Label className="text-xs uppercase tracking-wide text-muted-foreground">
      {text}
      {count !== undefined && <span className="normal-case"> ({count})</span>}
    </Label>
  );

  return (
    <Dialog open={!!fieldKey} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Ajouter des destinataires{fieldKey ? ` — champ ${FIELD_META[fieldKey].label}` : ""}</DialogTitle>
        </DialogHeader>

        <Input
          placeholder="Rechercher une liste, une catégorie, un adhérent, un contact…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-1">
          <div>
            {sectionLabel("Listes de diffusion", visibleLists.length)}
            <div className="divide-y mt-1">
              {visibleLists.length === 0 && <p className="text-xs text-muted-foreground py-2">Aucune liste</p>}
              {visibleLists.map((l) => {
                const entries = listEntries(l);
                return groupRow(`list-${l.id}`, l.name, entries.length, () => entries);
              })}
            </div>
          </div>

          <div>
            {sectionLabel("Catégories du carnet d'adresses", visibleCategories.length)}
            <div className="divide-y mt-1">
              {visibleCategories.length === 0 && <p className="text-xs text-muted-foreground py-2">Aucune catégorie</p>}
              {visibleCategories.map((cat) => {
                const entries = contacts
                  .filter((c) => c.category_id === cat.id && c.email)
                  .map((c) => ({ email: c.email, label: contactLabel(c), origin: `Carnet — ${cat.name}` }));
                return groupRow(`cat-${cat.id}`, cat.name, entries.length, () => entries);
              })}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between gap-2">
              {sectionLabel("Adhérents avec adresse mail", mailMembers.length)}
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={mailMembers.length === 0}
                onClick={() =>
                  push(mailMembers.map((m: any) => ({ email: m.email, label: memberLabel(m), origin: "Adhérents" })))
                }
              >
                Tout ajouter
              </Button>
            </div>
            <div className="border rounded-md max-h-56 overflow-y-auto divide-y mt-1">
              {mailMembers.length === 0 && <p className="text-xs text-muted-foreground p-2">Aucun adhérent</p>}
              {mailMembers.map((m: any) => (
                <button
                  key={m.id}
                  type="button"
                  className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted/50"
                  onClick={() => push([{ email: m.email, label: memberLabel(m), origin: "Adhérent" }])}
                >
                  <span className="font-medium">{memberLabel(m)}</span>{" "}
                  <span className="text-muted-foreground text-xs">{m.email}</span>
                  {m._isInactive && <Badge variant="outline" className="ml-1 text-[10px]">inactif</Badge>}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between gap-2">
              {sectionLabel("Carnet d'adresses", mailContacts.length)}
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={mailContacts.length === 0}
                onClick={() =>
                  push(mailContacts.map((c) => ({ email: c.email, label: contactLabel(c), origin: "Carnet d'adresses" })))
                }
              >
                Tout ajouter
              </Button>
            </div>
            <div className="border rounded-md max-h-56 overflow-y-auto divide-y mt-1">
              {mailContacts.length === 0 && <p className="text-xs text-muted-foreground p-2">Aucun contact</p>}
              {mailContacts.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className="w-full text-left px-2 py-1.5 text-sm hover:bg-muted/50"
                  onClick={() => push([{ email: c.email, label: contactLabel(c), origin: "Carnet d'adresses" }])}
                >
                  <Badge variant="secondary" className="mr-1.5 text-[10px]">Externe</Badge>
                  <span className="font-medium">{contactLabel(c)}</span>{" "}
                  <span className="text-muted-foreground text-xs">{c.email}</span>
                  {c.organization && <span className="text-muted-foreground text-xs"> · {c.organization}</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Terminé</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ComposeTab({
  draft,
  onDraftConsumed,
  prefill,
  onPrefillConsumed,
}: {
  draft: EmailCampaign | null;
  onDraftConsumed: () => void;
  prefill: ComposePrefill | null;
  onPrefillConsumed: () => void;
}) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [draftId, setDraftId] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("<p>Bonjour,</p><p></p>");
  const [smtpProfileId, setSmtpProfileId] = useState<string>("");

  const [fields, setFields] = useState<Record<FieldKey, FieldState>>({
    to: emptyField(),
    cc: emptyField(),
    bcc: emptyField(),
  });
  const [pickerField, setPickerField] = useState<FieldKey | null>(null);

  const { data: members = [] } = useMembersWithStatus();
  const { data: contacts = [] } = useContacts();
  const { data: contactCategories = [] } = useContactCategories();
  const { data: templates = [] } = useQuery({
    queryKey: ["email_templates"],
    queryFn: async () => asArray<any>((await supabase.from("email_templates").select("*").order("name")).data),
  });
  const { data: signatures = [] } = useSignatures();
  const [signatureId, setSignatureId] = useState("");
  const { data: smtpProfiles = [] } = useQuery({
    queryKey: ["smtp_profiles"],
    queryFn: async () => asArray<any>((await supabase.from("smtp_profiles").select("*").order("sort_order")).data),
  });
  const { data: lists = [] } = useQuery<MailingList[]>({
    queryKey: ["mailing_lists"],
    queryFn: async () => asArray<MailingList>((await supabase.from("mailing_lists").select("*").order("kind").order("name")).data),
  });
  const { data: allListMembers = [] } = useQuery({
    queryKey: ["mailing_list_members", "all"],
    queryFn: async () =>
      asArray<{
        list_id: string;
        member_id: string | null;
        contact_id: string | null;
      }>((await supabase.from("mailing_list_members").select("list_id,member_id,contact_id")).data),
  });

  const memberLabel = (m: any) => [m.first_name, m.last_name].filter(Boolean).join(" ") || m.email;
  const contactLabel = (c: Contact) =>
    [c.first_name, c.last_name].filter(Boolean).join(" ") || c.organization || c.email;

  const setField = (key: FieldKey, next: FieldState) => setFields((f) => ({ ...f, [key]: next }));

  const addToField = (key: FieldKey, entries: RecipientEntry[]) =>
    setFields((f) => {
      const seen = new Set(f[key].picked.map((p) => p.email));
      const merged = [...f[key].picked];
      for (const e of entries) {
        const email = e.email.trim().toLowerCase();
        if (seen.has(email)) continue;
        seen.add(email);
        merged.push({ ...e, email });
      }
      return { ...f, [key]: { ...f[key], picked: merged } };
    });

  const resetRecipients = () =>
    setFields({ to: emptyField(), cc: emptyField(), bcc: emptyField() });

  // Charge un brouillon dans le formulaire si demandé
  useEffect(() => {
    if (!draft) return;
    setDraftId(draft.id);
    setSubject(draft.subject ?? "");
    setBody(draft.body_html ?? "<p></p>");
    setSmtpProfileId(draft.smtp_profile_id ?? "");
    setFields({
      to: { picked: [], text: normalizeRecipients(draft.recipient_emails).join(", ") },
      cc: { picked: [], text: normalizeRecipients(draft.cc_emails ?? null).join(", ") },
      bcc: { picked: [], text: normalizeRecipients(draft.bcc_emails ?? null).join(", ") },
    });
    onDraftConsumed();
    toast.success(`Brouillon "${draft.subject || draft.name}" chargé`);
  }, [draft, onDraftConsumed]);

  // Pré-remplissage depuis le carnet d'adresses (champ « À »)
  useEffect(() => {
    if (!prefill) return;
    const entries: RecipientEntry[] = [];
    if (prefill.contactIds?.length) {
      const ids = new Set(prefill.contactIds);
      contacts
        .filter((c) => ids.has(c.id) && c.email)
        .forEach((c) => entries.push({ email: c.email, label: contactLabel(c), origin: "Carnet d'adresses" }));
    }
    if (prefill.emails?.length) {
      prefill.emails.forEach((e) => entries.push({ email: e, label: e, origin: "Saisie libre" }));
    }
    if (entries.length) addToField("to", entries);
    onPrefillConsumed();
    const n = entries.length;
    toast.success(`${n} destinataire${n > 1 ? "s" : ""} ajouté${n > 1 ? "s" : ""} au message`);
  }, [prefill, onPrefillConsumed, contacts]);

  const resetForm = () => {
    setDraftId(null);
    setSubject("");
    setBody("<p>Bonjour,</p><p></p>");
    resetRecipients();
  };

  useEffect(() => {
    if (smtpProfileId === "" && smtpProfiles.length > 0) {
      const def = smtpProfiles.find((p: any) => p.is_default) ?? smtpProfiles[0];
      if (def?.id) setSmtpProfileId(def.id);
    }
  }, [smtpProfiles, smtpProfileId]);

  const fieldEmails = (key: FieldKey) =>
    Array.from(new Set([...fields[key].picked.map((p) => p.email), ...parseEmails(fields[key].text)]));

  const toList = useMemo(() => fieldEmails("to"), [fields]);
  const ccList = useMemo(() => fieldEmails("cc").filter((e) => !toList.includes(e)), [fields, toList]);
  const bccList = useMemo(
    () => fieldEmails("bcc").filter((e) => !toList.includes(e) && !ccList.includes(e)),
    [fields, toList, ccList],
  );
  const total = toList.length + ccList.length + bccList.length;

  const payload = () => ({
    name: subject || "(sans nom)",
    subject,
    body_html: body,
    // Le champ « À » est volontairement visible de tous : pas d'envoi masqué.
    is_mass_mailing: false,
    recipient_emails: toList,
    cc_emails: ccList,
    bcc_emails: bccList,
    smtp_profile_id: smtpProfileId || null,
    created_by: user?.id,
  });

  const saveDraft = useMutation({
    mutationFn: async () => {
      if (!subject) throw new Error("L'objet est requis pour enregistrer un brouillon");
      if (draftId) {
        await api.patch(`/api/email-campaigns/${draftId}`, payload());
      } else {
        const res = await api.post<{ data: EmailCampaign }>("/api/email-campaigns", { ...payload(), status: "draft" });
        const newId = (res as any)?.data?.id ?? (res as any)?.id;
        if (newId) setDraftId(newId);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email_campaigns"] });
      toast.success("Brouillon enregistré");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const send = useMutation({
    mutationFn: async () => {
      if (draftId) {
        await api.patch(`/api/email-campaigns/${draftId}`, payload());
        const res = await api.post<{ send_error: string | null; data: EmailCampaign }>(`/api/email-campaigns/${draftId}/send`);
        if ((res as any)?.send_error) throw new Error((res as any).send_error);
      } else {
        const res = await api.post<{ send_error: string | null; data: EmailCampaign }>("/api/email-campaigns", payload());
        if ((res as any)?.send_error) throw new Error((res as any).send_error);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email_campaigns"] });
      toast.success(`Campagne envoyée à ${total} destinataire${total > 1 ? "s" : ""}.`);
      resetForm();
    },
    onError: (e: any) => {
      qc.invalidateQueries({ queryKey: ["email_campaigns"] });
      toast.error(e.message || "Échec de l'envoi");
    },
  });

  const loadTemplate = (id: string) => {
    const t: any = templates.find((x: any) => x.id === id);
    if (t) { setSubject(t.subject); setBody(t.body_html); toast.success(`Modèle "${t.name}" chargé`); }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-4">
        <Card className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium">Destinataires</h3>
            <Badge variant="secondary">{total}</Badge>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <RecipientField
              fieldKey="to"
              state={fields.to}
              onChange={(n) => setField("to", n)}
              onOpenPicker={() => setPickerField("to")}
              emails={toList}
            />
            <RecipientField
              fieldKey="cc"
              state={fields.cc}
              onChange={(n) => setField("cc", n)}
              onOpenPicker={() => setPickerField("cc")}
              emails={ccList}
            />
            <RecipientField
              fieldKey="bcc"
              state={fields.bcc}
              onChange={(n) => setField("bcc", n)}
              onOpenPicker={() => setPickerField("bcc")}
              emails={bccList}
            />
          </div>
        </Card>

        <Card className="p-6 space-y-4">
          {smtpProfiles.length > 0 && (
            <div className="space-y-1.5">
              <Label>Expéditeur</Label>
              <select
                className="w-full border rounded-md h-9 px-3 bg-background"
                value={smtpProfileId}
                onChange={(e) => setSmtpProfileId(e.target.value)}
              >
                {smtpProfiles.map((p: any) => (
                  <option key={p.id} value={p.id}>
                    {p.role_name}
                    {p.from_email ? ` — ${p.from_email}` : ""}
                    {p.is_default ? "  (défaut)" : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
          {smtpProfiles.length === 0 && (
            <Card className="p-3 bg-warning/10 border-warning/30 text-sm flex gap-2">
              <AlertCircle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
              <div>
                Aucun profil SMTP configuré. Rendez-vous dans <em>Administration → Paramètres SMTP</em>
                pour créer une adresse d'envoi.
              </div>
            </Card>
          )}
          {templates.length > 0 && (
            <div className="space-y-1.5">
              <Label>Charger un modèle</Label>
              <select className="w-full border rounded-md h-9 px-3 bg-background" onChange={(e) => e.target.value && loadTemplate(e.target.value)} defaultValue="">
                <option value="">— Choisir un modèle —</option>
                {templates.map((t: any) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}
          {signatures.length > 0 && (
            <div className="space-y-1.5">
              <Label>Signature</Label>
              <select
                className="w-full border rounded-md h-9 px-3 bg-background"
                value={signatureId}
                onChange={(e) => {
                  const id = e.target.value;
                  setSignatureId(id);
                  setBody((prev) => applySignature(prev, signatures.find((s) => s.id === id) ?? null));
                }}
              >
                <option value="">— Aucune signature —</option>
                {signatures.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}{s.is_default ? " (défaut)" : ""}</option>
                ))}
              </select>
            </div>
          )}
          <div className="space-y-1.5"><Label>Objet *</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} required /></div>
          <div className="space-y-1.5"><Label>Message *</Label><RichTextEditor value={body} onChange={setBody} /></div>

          {draftId && <div><Badge variant="outline">Brouillon en cours</Badge></div>}

          {(() => {
            const blockers: string[] = [];
            if (!subject) blockers.push("renseigner un objet");
            if (total === 0) blockers.push("ajouter au moins un destinataire");
            if (smtpProfiles.length === 0) blockers.push("configurer un profil SMTP (Administration → SMTP)");
            const blocked = blockers.length > 0;
            return (
              <>
                <div className="flex flex-wrap gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (!subject) { toast.error("Renseignez un objet pour enregistrer le brouillon"); return; }
                      saveDraft.mutate();
                    }}
                    disabled={saveDraft.isPending}
                  >
                    <FilePen className="h-4 w-4 mr-2" />Brouillon
                  </Button>
                  <Button
                    onClick={() => {
                      if (blocked) { toast.error("Avant d'envoyer : " + blockers.join(", ")); return; }
                      toast.info(`Envoi en cours vers ${total} destinataire${total > 1 ? "s" : ""}…`);
                      send.mutate();
                    }}
                    disabled={send.isPending}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {send.isPending ? "Envoi…" : "Envoyer"}
                  </Button>
                </div>
                {blocked && (
                  <p className="text-xs text-warning flex gap-1.5">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span>Pour envoyer : {blockers.join(" · ")}.</span>
                  </p>
                )}
              </>
            );
          })()}
          {draftId && (
            <Button variant="ghost" size="sm" className="w-full" onClick={resetForm}>
              Nouveau mail (abandonner ce brouillon)
            </Button>
          )}
        </Card>
      </div>


      <RecipientPicker
        fieldKey={pickerField}
        onClose={() => setPickerField(null)}
        onAdd={(entries) => pickerField && addToField(pickerField, entries)}
        members={members}
        contacts={contacts}
        contactCategories={contactCategories}
        lists={lists}
        allListMembers={allListMembers}
        memberLabel={memberLabel}
        contactLabel={contactLabel}
      />
    </div>
  );
}

// ============ LISTES DE DIFFUSION ============
function ListsTab() {
  const qc = useQueryClient();
  const { hasAccess } = useAuth();
  const canWrite = hasAccess("mailing", "write");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<{ id?: string; name: string; description: string }>({ name: "", description: "" });
  const [managingList, setManagingList] = useState<MailingList | null>(null);

  const { data: lists = [] } = useQuery<MailingList[]>({
    queryKey: ["mailing_lists"],
    queryFn: async () => asArray<MailingList>((await supabase.from("mailing_lists").select("*").order("kind").order("name")).data),
  });

  const { data: members = [] } = useMembersWithStatus();

  // Compteurs : pour les dynamiques on calcule, pour les manuelles on requête
  const { data: manualCounts = {} } = useQuery({
    queryKey: ["mailing_list_counts"],
    queryFn: async () => {
      const { data } = await supabase.from("mailing_list_members").select("list_id");
      const counts: Record<string, number> = {};
      for (const r of asArray<any>(data)) counts[r.list_id] = (counts[r.list_id] ?? 0) + 1;
      return counts;
    },
  });

  const { data: contacts = [] } = useContacts();
  const { data: allListMembers = [] } = useQuery({
    queryKey: ["mailing_list_members", "all"],
    queryFn: async () =>
      asArray<{
        list_id: string;
        member_id: string | null;
        contact_id: string | null;
      }>((await supabase.from("mailing_list_members").select("list_id,member_id,contact_id")).data),
  });

  const dynamicActive = members.filter((m: any) => m.email && !m._isInactive).length;
  const dynamicInactive = members.filter((m: any) => m.email && m._isInactive).length;

  // Adresses réelles d'une liste (pour l'export CSV)
  const listEntries = (list: MailingList): { label: string; email: string }[] => {
    const mLabel = (m: any) => [m.first_name, m.last_name].filter(Boolean).join(" ") || m.email;
    const cLabel = (c: Contact) => [c.first_name, c.last_name].filter(Boolean).join(" ") || c.organization || c.email;
    if (list.kind === "dynamic_active")
      return members.filter((m: any) => m.email && !m._isInactive).map((m: any) => ({ label: mLabel(m), email: m.email }));
    if (list.kind === "dynamic_inactive")
      return members.filter((m: any) => m.email && m._isInactive).map((m: any) => ({ label: mLabel(m), email: m.email }));
    const rows = allListMembers.filter((r) => r.list_id === list.id);
    const mids = new Set(rows.map((r) => r.member_id).filter(Boolean) as string[]);
    const cids = new Set(rows.map((r) => r.contact_id).filter(Boolean) as string[]);
    return [
      ...members.filter((m: any) => m.email && mids.has(m.id)).map((m: any) => ({ label: mLabel(m), email: m.email })),
      ...contacts.filter((c) => c.email && cids.has(c.id)).map((c) => ({ label: cLabel(c), email: c.email })),
    ];
  };

  const save = useMutation({
    mutationFn: async () => {
      if (!editing.name.trim()) throw new Error("Nom requis");
      if (editing.id) {
        const { error } = await supabase.from("mailing_lists").update({
          name: editing.name, description: editing.description || null,
        }).eq("id", editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("mailing_lists").insert({
          name: editing.name, description: editing.description || null, kind: "manual",
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mailing_lists"] });
      toast.success("Liste enregistrée");
      setOpen(false);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("mailing_lists").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mailing_lists"] });
      qc.invalidateQueries({ queryKey: ["mailing_list_counts"] });
      toast.success("Liste supprimée");
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-muted-foreground">
          Les listes <strong>dynamiques</strong> sont recalculées à chaque envoi. Les listes <strong>manuelles</strong> contiennent une sélection d'adhérents.
        </p>
        {canWrite && (
          <Button onClick={() => { setEditing({ name: "", description: "" }); setOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" />Nouvelle liste
          </Button>
        )}
      </div>
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Description</TableHead>
              <TableHead className="text-right">Destinataires</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lists.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Aucune liste</TableCell></TableRow>}
            {lists.map((l) => {
              const count = l.kind === "dynamic_active"
                ? dynamicActive
                : l.kind === "dynamic_inactive"
                ? dynamicInactive
                : (manualCounts[l.id] ?? 0);
              return (
                <TableRow key={l.id}>
                  <TableCell className="font-medium flex items-center gap-2">
                    {l.kind !== "manual" && <Sparkles className="h-3.5 w-3.5 text-primary" />}
                    {l.name}
                  </TableCell>
                  <TableCell>
                    {l.kind === "manual" ? (
                      <Badge variant="outline">Manuelle</Badge>
                    ) : (
                      <Badge variant="secondary">Dynamique</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">{l.description ?? "—"}</TableCell>
                  <TableCell className="text-right">
                    <Badge variant="secondary">{count}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Exporter les adresses en CSV"
                      onClick={() => {
                        const entries = listEntries(l);
                        if (entries.length === 0) return toast.error("Aucune adresse dans cette liste");
                        downloadCsv(entries, `liste-${l.name.replace(/[^\w-]+/g, "-").toLowerCase()}`);
                      }}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    {l.kind === "manual" && canWrite && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => setManagingList(l)} title="Gérer les membres">
                          <Users className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setEditing({ id: l.id, name: l.name, description: l.description ?? "" }); setOpen(true); }}>
                          Modifier
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { if (confirm(`Supprimer la liste "${l.name}" ?`)) del.mutate(l.id); }}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </>
                    )}
                    {l.kind !== "manual" && (
                      <span className="text-xs text-muted-foreground">Auto</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing.id ? "Modifier la liste" : "Nouvelle liste"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Nom *</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={() => save.mutate()} disabled={!editing.name}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ManageListMembersDialog list={managingList} onClose={() => setManagingList(null)} />
    </>
  );
}

function ManageListMembersDialog({ list, onClose }: { list: MailingList | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const { data: members = [] } = useMembersWithStatus();
  const { data: contacts = [] } = useContacts();

  const { data: listMembers = [] } = useQuery({
    queryKey: ["mailing_list_members", list?.id],
    queryFn: async () => {
      if (!list) return [];
      return ((await supabase.from("mailing_list_members").select("member_id,contact_id").eq("list_id", list.id)).data ?? []) as { member_id: string | null; contact_id: string | null }[];
    },
    enabled: !!list,
  });

  const memberIds = useMemo(() => new Set(listMembers.map((x) => x.member_id).filter(Boolean) as string[]), [listMembers]);
  const contactIds = useMemo(() => new Set(listMembers.map((x) => x.contact_id).filter(Boolean) as string[]), [listMembers]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["mailing_list_members", list?.id] });
    qc.invalidateQueries({ queryKey: ["mailing_list_counts"] });
  };

  const toggle = useMutation({
    mutationFn: async (vars: { memberId: string; add: boolean }) => {
      if (!list) return;
      if (vars.add) {
        const { error } = await supabase.from("mailing_list_members").insert({ list_id: list.id, member_id: vars.memberId });
        if (error) throw error;
      } else {
        const { error } = await supabase.from("mailing_list_members").delete().eq("list_id", list.id).eq("member_id", vars.memberId);
        if (error) throw error;
      }
    },
    onSuccess: invalidate,
    onError: (e: any) => toast.error(e.message),
  });

  const toggleContact = useMutation({
    mutationFn: async (vars: { contactId: string; add: boolean }) => {
      if (!list) return;
      if (vars.add) {
        await api.post("/api/mailing-list-members", { list_id: list.id, contact_id: vars.contactId }, { rawJson: true });
      } else {
        await api.delete("/api/mailing-list-members", { query: { list_id: list.id, contact_id: vars.contactId } });
      }
    },
    onSuccess: invalidate,
    onError: (e: any) => toast.error(e.message),
  });

  const q = search.trim().toLowerCase();
  const inListMembers = members.filter((m: any) => memberIds.has(m.id));
  const searchResults = q
    ? members
        .filter((m: any) => !memberIds.has(m.id))
        .filter((m: any) => `${m.first_name} ${m.last_name} ${m.email ?? ""}`.toLowerCase().includes(q))
        .slice(0, 20)
    : [];

  const cq = contactSearch.trim().toLowerCase();
  const inListContacts = contacts.filter((c) => contactIds.has(c.id));
  const contactResults = cq
    ? contacts
        .filter((c) => !contactIds.has(c.id))
        .filter((c) => `${c.first_name ?? ""} ${c.last_name ?? ""} ${c.organization ?? ""} ${c.job_title ?? ""} ${c.email}`.toLowerCase().includes(cq))
        .slice(0, 20)
    : [];

  const contactLabel = (c: Contact) =>
    [c.first_name, c.last_name].filter(Boolean).join(" ") || c.organization || c.email;

  return (
    <Dialog open={!!list} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Destinataires de « {list?.name} »</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Ajouter un adhérent</Label>
            <Input placeholder="Rechercher un adhérent à ajouter…" value={search} onChange={(e) => setSearch(e.target.value)} />
            {q && (
              <div className="max-h-52 overflow-y-auto border rounded-md divide-y">
                {searchResults.length === 0 && (
                  <div className="text-sm text-muted-foreground py-3 px-3">Aucun adhérent trouvé</div>
                )}
                {searchResults.map((m: any) => (
                  <button
                    key={m.id}
                    type="button"
                    className="w-full text-left flex items-center gap-2 text-sm py-2 px-3 hover:bg-muted/40"
                    onClick={() => { toggle.mutate({ memberId: m.id, add: true }); setSearch(""); }}
                  >
                    <Plus className="h-3.5 w-3.5 text-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate">
                        {m.first_name} {m.last_name}
                        {m._isInactive && <Badge variant="outline" className="ml-1 text-[10px]">inactif</Badge>}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{m.email ?? "Pas d'email"}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Ajouter un contact du carnet d'adresses</Label>
            <Input placeholder="Rechercher un contact externe…" value={contactSearch} onChange={(e) => setContactSearch(e.target.value)} />
            {cq && (
              <div className="max-h-52 overflow-y-auto border rounded-md divide-y">
                {contactResults.length === 0 && (
                  <div className="text-sm text-muted-foreground py-3 px-3">Aucun contact trouvé</div>
                )}
                {contactResults.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className="w-full text-left flex items-center gap-2 text-sm py-2 px-3 hover:bg-muted/40"
                    onClick={() => { toggleContact.mutate({ contactId: c.id, add: true }); setContactSearch(""); }}
                  >
                    <Plus className="h-3.5 w-3.5 text-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate">
                        {contactLabel(c)}
                        <Badge variant="outline" className="ml-1 text-[10px]">contact</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {[c.job_title, c.organization].filter(Boolean).join(" — ")}{c.job_title || c.organization ? " · " : ""}{c.email}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="text-sm text-muted-foreground">
            <Badge variant="secondary">{memberIds.size}</Badge> adhérent{memberIds.size > 1 ? "s" : ""}
            {" "}et <Badge variant="secondary">{contactIds.size}</Badge> contact{contactIds.size > 1 ? "s" : ""} dans la liste
          </div>
          <div className="max-h-96 overflow-y-auto border rounded-md divide-y">
            {inListMembers.length === 0 && inListContacts.length === 0 && (
              <div className="text-sm text-muted-foreground py-6 px-3 text-center">
                Aucun destinataire dans cette liste — utilisez les recherches ci-dessus pour en ajouter.
              </div>
            )}
            {inListMembers.map((m: any) => (
              <div key={m.id} className="flex items-center gap-2 text-sm py-2 px-3">
                <div className="flex-1 min-w-0">
                  <div className="truncate">
                    {m.first_name} {m.last_name}
                    {m._isInactive && <Badge variant="outline" className="ml-1 text-[10px]">inactif</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{m.email ?? "Pas d'email"}</div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  title="Retirer de la liste"
                  onClick={() => toggle.mutate({ memberId: m.id, add: false })}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
            {inListContacts.map((c) => (
              <div key={c.id} className="flex items-center gap-2 text-sm py-2 px-3">
                <div className="flex-1 min-w-0">
                  <div className="truncate">
                    {contactLabel(c)}
                    <Badge variant="outline" className="ml-1 text-[10px]">contact</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {[c.job_title, c.organization].filter(Boolean).join(" — ")}{c.job_title || c.organization ? " · " : ""}{c.email}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  title="Retirer de la liste"
                  onClick={() => toggleContact.mutate({ contactId: c.id, add: false })}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function TemplatesTab() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<{ id?: string; name: string; subject: string; body_html: string }>({ name: "", subject: "", body_html: "<p></p>" });

  const { data: templates = [] } = useQuery({
    queryKey: ["email_templates"],
    queryFn: async () => asArray<any>((await supabase.from("email_templates").select("*").order("name")).data),
  });

  const save = useMutation({
    mutationFn: async () => {
      if (editing.id) {
        const { error } = await supabase.from("email_templates").update({ name: editing.name, subject: editing.subject, body_html: editing.body_html }).eq("id", editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("email_templates").insert({ name: editing.name, subject: editing.subject, body_html: editing.body_html });
        if (error) throw error;
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["email_templates"] }); toast.success("Modèle enregistré"); setOpen(false); },
    onError: (e: any) => toast.error(e.message),
  });
  const del = useMutation({
    mutationFn: async (id: string) => { const { error } = await supabase.from("email_templates").delete().eq("id", id); if (error) throw error; },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["email_templates"] }); toast.success("Supprimé"); },
  });

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={() => { setEditing({ name: "", subject: "", body_html: "<p></p>" }); setOpen(true); }}><Plus className="h-4 w-4 mr-2" />Nouveau modèle</Button>
      </div>
      <Card>
        <Table>
          <TableHeader><TableRow><TableHead>Nom</TableHead><TableHead>Objet</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {templates.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-8">Aucun modèle</TableCell></TableRow>}
            {templates.map((t: any) => (
              <TableRow key={t.id}>
                <TableCell className="font-medium">{t.name}</TableCell>
                <TableCell className="text-muted-foreground">{t.subject}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => { setEditing(t); setOpen(true); }}>Modifier</Button>
                  <Button variant="ghost" size="sm" onClick={() => { if (confirm("Supprimer ce modèle ?")) del.mutate(t.id); }}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{editing.id ? "Modifier le modèle" : "Nouveau modèle"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Nom du modèle *</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Objet *</Label><Input value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Contenu *</Label><RichTextEditor value={editing.body_html} onChange={(html) => setEditing({ ...editing, body_html: html })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={() => save.mutate()} disabled={!editing.name || !editing.subject}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SignaturesTab() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<{ id?: string; name: string; body_html: string; smtp_profile_id: string | null; is_default: boolean }>(
    { name: "", body_html: "<p></p>", smtp_profile_id: null, is_default: false },
  );

  const { data: signatures = [] } = useSignatures();
  const { data: smtpProfiles = [] } = useQuery({
    queryKey: ["smtp_profiles"],
    queryFn: async () => asArray<any>((await supabase.from("smtp_profiles").select("*").order("sort_order")).data),
  });

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        name: editing.name,
        body_html: editing.body_html,
        smtp_profile_id: editing.smtp_profile_id,
        is_default: editing.is_default,
      };
      if (editing.id) await api.patch(`/api/email-signatures/${editing.id}`, payload);
      else await api.post("/api/email-signatures", payload);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["email_signatures"] }); toast.success("Signature enregistrée"); setOpen(false); },
    onError: (e: any) => toast.error(e.message || "Enregistrement impossible"),
  });

  const del = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/api/email-signatures/${id}`); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["email_signatures"] }); toast.success("Signature supprimée"); },
    onError: (e: any) => toast.error(e.message || "Suppression impossible"),
  });

  const profileName = (id: string | null) => {
    const p: any = smtpProfiles.find((x: any) => x.id === id);
    return p ? `${p.role_name}${p.from_email ? ` — ${p.from_email}` : ""}` : "Toutes les adresses";
  };

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={() => { setEditing({ name: "", body_html: "<p></p>", smtp_profile_id: null, is_default: false }); setOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" />Nouvelle signature
        </Button>
      </div>
      <Card>
        <Table>
          <TableHeader>
            <TableRow><TableHead>Nom</TableHead><TableHead>Adresse d'envoi</TableHead><TableHead>Par défaut</TableHead><TableHead></TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {signatures.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Aucune signature</TableCell></TableRow>
            )}
            {signatures.map((s) => (
              <TableRow key={s.id}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell className="text-muted-foreground">{profileName(s.smtp_profile_id)}</TableCell>
                <TableCell>{s.is_default ? <Badge variant="default">Par défaut</Badge> : null}</TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEditing({
                        id: s.id, name: s.name, body_html: s.body_html,
                        smtp_profile_id: s.smtp_profile_id, is_default: !!s.is_default,
                      });
                      setOpen(true);
                    }}
                  >
                    Modifier
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { if (confirm("Supprimer cette signature ?")) del.mutate(s.id); }}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[95vw] max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing.id ? "Modifier la signature" : "Nouvelle signature"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Nom *</Label>
              <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Signature président" />
            </div>
            <div className="space-y-1.5">
              <Label>Adresse d'envoi associée</Label>
              <select
                className="w-full border rounded-md h-9 px-3 bg-background"
                value={editing.smtp_profile_id ?? ""}
                onChange={(e) => setEditing({ ...editing, smtp_profile_id: e.target.value || null })}
              >
                <option value="">Toutes les adresses</option>
                {smtpProfiles.map((p: any) => (
                  <option key={p.id} value={p.id}>{p.role_name}{p.from_email ? ` — ${p.from_email}` : ""}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                La signature sera proposée automatiquement quand vous écrivez depuis cette adresse.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="sig-default"
                checked={editing.is_default}
                onCheckedChange={(v) => setEditing({ ...editing, is_default: v === true })}
              />
              <Label htmlFor="sig-default" className="cursor-pointer">Utiliser par défaut</Label>
            </div>
            <div className="space-y-1.5">
              <Label>Contenu *</Label>
              <RichTextEditor value={editing.body_html} onChange={(html) => setEditing({ ...editing, body_html: html })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={() => save.mutate()} disabled={!editing.name || save.isPending}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function statusBadge(status: string) {
  if (status === "sent") return <Badge variant="default">Envoyé</Badge>;
  if (status === "failed") return <Badge variant="destructive">Échec</Badge>;
  if (status === "sending") return <Badge variant="secondary">En cours</Badge>;
  if (status === "draft") return <Badge variant="outline">Brouillon</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}

function useCampaigns() {
  return useQuery<EmailCampaign[]>({
    queryKey: ["email_campaigns"],
    queryFn: async () =>
      ((await supabase.from("email_campaigns").select("*").order("created_at", { ascending: false }).limit(200)).data ?? []) as any,
  });
}

function DraftsTab({ onEdit }: { onEdit: (c: EmailCampaign) => void }) {
  const qc = useQueryClient();
  const { data: campaigns = [] } = useCampaigns();
  const drafts = campaigns.filter((c) => c.status === "draft");

  const del = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/api/email-campaigns/${id}`); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["email_campaigns"] }); toast.success("Brouillon supprimé"); },
    onError: (e: any) => toast.error(e.message),
  });

  const sendNow = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<{ send_error: string | null }>(`/api/email-campaigns/${id}/send`);
      if ((res as any)?.send_error) throw new Error((res as any).send_error);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["email_campaigns"] }); toast.success("Brouillon envoyé"); },
    onError: (e: any) => { qc.invalidateQueries({ queryKey: ["email_campaigns"] }); toast.error(e.message || "Échec de l'envoi"); },
  });

  return (
    <Card>
      <Table>
        <TableHeader><TableRow>
          <TableHead>Date</TableHead><TableHead>Objet</TableHead><TableHead>Destinataires</TableHead><TableHead className="text-right">Actions</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {drafts.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Aucun brouillon</TableCell></TableRow>}
          {drafts.map((c) => {
            const rcpts = normalizeRecipients(c.recipient_emails);
            return (
              <TableRow key={c.id}>
                <TableCell>{new Date(c.created_at).toLocaleDateString("fr-FR")}</TableCell>
                <TableCell className="font-medium">{c.subject || <em className="text-muted-foreground">(sans objet)</em>}</TableCell>
                <TableCell>{rcpts.length}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => onEdit(c)}>Reprendre</Button>
                  <Button variant="ghost" size="sm" onClick={() => sendNow.mutate(c.id)} disabled={rcpts.length === 0 || sendNow.isPending}>
                    <Send className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { if (confirm("Supprimer ce brouillon ?")) del.mutate(c.id); }}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}

function HistoryTab() {
  const qc = useQueryClient();
  const { data: campaigns = [] } = useCampaigns();
  const [filter, setFilter] = useState<"all" | "sent" | "failed">("all");
  const [detail, setDetail] = useState<EmailCampaign | null>(null);

  const items = campaigns
    .filter((c) => c.status !== "draft")
    .filter((c) => filter === "all" ? true : c.status === filter);

  const failedCount = campaigns.filter((c) => c.status === "failed").length;

  const retry = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post<{ send_error: string | null }>(`/api/email-campaigns/${id}/send`);
      if ((res as any)?.send_error) throw new Error((res as any).send_error);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["email_campaigns"] }); toast.success("Envoi réussi"); },
    onError: (e: any) => { qc.invalidateQueries({ queryKey: ["email_campaigns"] }); toast.error(e.message || "Nouvel échec"); },
  });

  const del = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/api/email-campaigns/${id}`); },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["email_campaigns"] }); toast.success("Supprimé"); },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex gap-2">
          <Button size="sm" variant={filter === "all" ? "default" : "outline"} onClick={() => setFilter("all")}>Tous</Button>
          <Button size="sm" variant={filter === "sent" ? "default" : "outline"} onClick={() => setFilter("sent")}>Envoyés</Button>
          <Button size="sm" variant={filter === "failed" ? "default" : "outline"} onClick={() => setFilter("failed")}>
            Anomalies {failedCount > 0 && <Badge variant="destructive" className="ml-1.5">{failedCount}</Badge>}
          </Button>
        </div>
      </div>
      <Card>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Date</TableHead><TableHead>Objet</TableHead><TableHead>Type</TableHead>
            <TableHead>Destinataires</TableHead><TableHead>Statut</TableHead><TableHead>Anomalie</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Aucune campagne</TableCell></TableRow>}
            {items.map((c) => {
              const rcpts = normalizeRecipients(c.recipient_emails);
              return (
                <TableRow key={c.id}>
                  <TableCell className="whitespace-nowrap text-sm">
                    {new Date(c.sent_at ?? c.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                  </TableCell>
                  <TableCell className="font-medium">{c.subject}</TableCell>
                  <TableCell>{c.is_mass_mailing ? "Newsletter" : "Personnalisé"}</TableCell>
                  <TableCell>{rcpts.length}</TableCell>
                  <TableCell>{statusBadge(c.status)}</TableCell>
                  <TableCell className="max-w-xs">
                    {c.last_error ? (
                      <span className="text-xs text-destructive truncate block" title={c.last_error}>{c.last_error}</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => setDetail(c)} title="Détails">
                      <Eye className="h-4 w-4" />
                    </Button>
                    {c.status === "failed" && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => retry.mutate(c.id)} disabled={retry.isPending} title="Réessayer">
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { if (confirm("Supprimer cette campagne en échec ?")) del.mutate(c.id); }}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{detail?.subject}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="flex flex-wrap gap-3 text-muted-foreground">
                <span>Statut : {statusBadge(detail.status)}</span>
                <span>Créé le {new Date(detail.created_at).toLocaleString("fr-FR")}</span>
                {detail.sent_at && <span>Envoyé le {new Date(detail.sent_at).toLocaleString("fr-FR")}</span>}
              </div>
              {detail.last_error && (
                <div className="border border-destructive/40 bg-destructive/5 rounded-md p-3">
                  <div className="font-medium text-destructive mb-1">Anomalie</div>
                  <div className="text-xs whitespace-pre-wrap break-words">{detail.last_error}</div>
                </div>
              )}
              <div>
                <div className="font-medium mb-1">Destinataires ({normalizeRecipients(detail.recipient_emails).length})</div>
                <div className="text-xs text-muted-foreground max-h-32 overflow-y-auto border rounded p-2 break-all">
                  {normalizeRecipients(detail.recipient_emails).join(", ") || "—"}
                </div>
              </div>
              {normalizeRecipients(detail.cc_emails ?? null).length > 0 && (
                <div>
                  <div className="font-medium mb-1">Cc ({normalizeRecipients(detail.cc_emails ?? null).length})</div>
                  <div className="text-xs text-muted-foreground border rounded p-2 break-all">
                    {normalizeRecipients(detail.cc_emails ?? null).join(", ")}
                  </div>
                </div>
              )}
              {normalizeRecipients(detail.bcc_emails ?? null).length > 0 && (
                <div>
                  <div className="font-medium mb-1">Cci ({normalizeRecipients(detail.bcc_emails ?? null).length})</div>
                  <div className="text-xs text-muted-foreground border rounded p-2 break-all">
                    {normalizeRecipients(detail.bcc_emails ?? null).join(", ")}
                  </div>
                </div>
              )}
              <div>
                <div className="font-medium mb-1">Contenu</div>
                <div className="border rounded p-3 max-h-72 overflow-y-auto prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: detail.body_html }} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============ CARNET D'ADRESSES (contacts externes) ============
interface ContactCategory {
  id: string;
  name: string;
  description: string | null;
}

interface Contact {
  id: string;
  category_id: string | null;
  category_name?: string | null;
  first_name: string | null;
  last_name: string | null;
  organization: string | null;
  job_title: string | null;
  email: string;
  phone: string | null;
  notes: string | null;
}

function useContactCategories() {
  return useQuery<ContactCategory[]>({
    queryKey: ["contact_categories"],
    queryFn: async () =>
      asArray<ContactCategory>((await supabase.from("contact_categories").select("*").order("name")).data),
  });
}

function useContacts() {
  return useQuery<Contact[]>({
    queryKey: ["contacts"],
    queryFn: async () =>
      asArray<Contact>((await supabase.from("contacts").select("*").order("last_name")).data),
  });
}

const emptyContact = {
  category_id: "",
  first_name: "",
  last_name: "",
  organization: "",
  job_title: "",
  email: "",
  phone: "",
  notes: "",
};

function ContactsTab({ onCompose }: { onCompose: (p: ComposePrefill) => void }) {
  const qc = useQueryClient();
  const { hasAccess } = useAuth();
  const canWrite = hasAccess("mailing", "write");

  const { data: contacts = [] } = useContacts();
  const { data: categories = [] } = useContactCategories();

  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [contactOpen, setContactOpen] = useState(false);
  const [editing, setEditing] = useState<{ id?: string } & typeof emptyContact>({ ...emptyContact });
  const [catOpen, setCatOpen] = useState(false);
  const [editingCat, setEditingCat] = useState<{ id?: string; name: string; description: string }>({ name: "", description: "" });
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["contacts"] });
    qc.invalidateQueries({ queryKey: ["contact_categories"] });
  };

  const saveContact = useMutation({
    mutationFn: async () => {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editing.email.trim())) throw new Error("Adresse email invalide");
      const body = {
        category_id: editing.category_id || null,
        first_name: editing.first_name || null,
        last_name: editing.last_name || null,
        organization: editing.organization || null,
        job_title: editing.job_title || null,
        email: editing.email.trim(),
        phone: editing.phone || null,
        notes: editing.notes || null,
      };
      if (editing.id) await api.patch(`/api/contacts/${editing.id}`, body, { rawJson: true });
      else await api.post("/api/contacts", body, { rawJson: true });
    },
    onSuccess: () => { invalidate(); toast.success("Contact enregistré"); setContactOpen(false); },
    onError: (e: any) => toast.error(e.message),
  });

  const delContact = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/api/contacts/${id}`); },
    onSuccess: () => { invalidate(); toast.success("Contact supprimé"); },
    onError: (e: any) => toast.error(e.message),
  });

  const saveCat = useMutation({
    mutationFn: async () => {
      if (!editingCat.name.trim()) throw new Error("Nom requis");
      const body = { name: editingCat.name.trim(), description: editingCat.description || null };
      if (editingCat.id) await api.patch(`/api/contact-categories/${editingCat.id}`, body, { rawJson: true });
      else await api.post("/api/contact-categories", body, { rawJson: true });
    },
    onSuccess: () => { invalidate(); toast.success("Catégorie enregistrée"); setCatOpen(false); },
    onError: (e: any) => toast.error(e.message),
  });

  const delCat = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/api/contact-categories/${id}`); },
    onSuccess: () => { invalidate(); toast.success("Catégorie supprimée"); },
    onError: (e: any) => toast.error(e.message),
  });

  const catName = (id: string | null) => categories.find((c) => c.id === id)?.name ?? null;

  const q = search.trim().toLowerCase();
  const rows = contacts
    .filter((c) => (filterCat ? c.category_id === filterCat : true))
    .filter((c) =>
      q ? `${c.first_name ?? ""} ${c.last_name ?? ""} ${c.organization ?? ""} ${c.email}`.toLowerCase().includes(q) : true,
    );

  const counts: Record<string, number> = {};
  for (const c of contacts) if (c.category_id) counts[c.category_id] = (counts[c.category_id] ?? 0) + 1;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Contacts externes (partenaires, institutions, presse…), indépendants des adhérents et des listes de diffusion.
        </p>
        {canWrite && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setEditingCat({ name: "", description: "" }); setCatOpen(true); }}>
              <Tags className="h-4 w-4 mr-2" />Nouvelle catégorie
            </Button>
            <Button onClick={() => { setEditing({ ...emptyContact }); setContactOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />Nouveau contact
            </Button>
          </div>
        )}
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="space-y-1.5 flex-1 min-w-[200px]">
            <Label className="text-xs">Recherche</Label>
            <Input placeholder="Nom, organisme, email…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="space-y-1.5 min-w-[200px]">
            <Label className="text-xs">Catégorie</Label>
            <select
              className="w-full border rounded-md h-9 px-3 bg-background text-sm"
              value={filterCat}
              onChange={(e) => setFilterCat(e.target.value)}
            >
              <option value="">Toutes</option>
              {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <Button
            variant="outline"
            onClick={async () => {
              const emails = rows.map((c) => c.email).filter(Boolean);
              if (emails.length === 0) return toast.error("Aucun contact");
              try {
                await navigator.clipboard.writeText(emails.join(", "));
                toast.success(`${emails.length} adresse(s) copiée(s)`);
              } catch { toast.error("Impossible de copier"); }
            }}
          >
            <Copy className="h-4 w-4 mr-1.5" />Copier les emails
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              const list = (picked.size > 0 ? rows.filter((c) => picked.has(c.id)) : rows).filter((c) => c.email);
              if (list.length === 0) return toast.error("Aucun contact");
              downloadCsv(
                list.map((c) => ({
                  label: [c.first_name, c.last_name].filter(Boolean).join(" ") || c.organization || c.email,
                  email: c.email,
                })),
                "carnet-adresses",
              );
            }}
          >
            <Download className="h-4 w-4 mr-1.5" />Export CSV
          </Button>
          <Button
            onClick={() => {
              const ids = picked.size > 0 ? Array.from(picked) : rows.map((c) => c.id);
              if (ids.length === 0) return toast.error("Aucun contact");
              onCompose({ contactIds: ids });
              setPicked(new Set());
            }}
          >
            <Send className="h-4 w-4 mr-1.5" />
            {picked.size > 0 ? `Écrire à la sélection (${picked.size})` : "Écrire à la liste filtrée"}
          </Button>
        </div>
      </Card>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={rows.length > 0 && rows.every((c) => picked.has(c.id))}
                  onCheckedChange={(v) => setPicked(v ? new Set(rows.map((c) => c.id)) : new Set())}
                  aria-label="Tout sélectionner"
                />
              </TableHead>
              <TableHead>Nom</TableHead>
              <TableHead>Organisme</TableHead>
              <TableHead>Fonction</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Téléphone</TableHead>
              <TableHead>Catégorie</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Aucun contact</TableCell></TableRow>
            )}
            {rows.map((c) => (
              <TableRow key={c.id}>
                <TableCell>
                  <Checkbox
                    checked={picked.has(c.id)}
                    onCheckedChange={(v) => {
                      const n = new Set(picked);
                      if (v) n.add(c.id); else n.delete(c.id);
                      setPicked(n);
                    }}
                    aria-label={`Sélectionner ${c.email}`}
                  />
                </TableCell>
                <TableCell className="font-medium">{[c.first_name, c.last_name].filter(Boolean).join(" ") || "—"}</TableCell>
                <TableCell className="text-muted-foreground">{c.organization ?? "—"}</TableCell>
                <TableCell className="text-muted-foreground">{c.job_title ?? "—"}</TableCell>
                <TableCell>{c.email}</TableCell>
                <TableCell className="text-muted-foreground">{c.phone ?? "—"}</TableCell>
                <TableCell>
                  {c.category_name ?? catName(c.category_id) ? (
                    <Badge variant="secondary">{c.category_name ?? catName(c.category_id)}</Badge>
                  ) : (
                    <span className="text-muted-foreground text-sm">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" title="Envoyer un mail à ce contact" onClick={() => onCompose({ contactIds: [c.id] })}>
                    <Send className="h-4 w-4" />
                  </Button>
                  {canWrite && (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => {
                        setEditing({
                          id: c.id,
                          category_id: c.category_id ?? "",
                          first_name: c.first_name ?? "",
                          last_name: c.last_name ?? "",
                          organization: c.organization ?? "",
                          job_title: c.job_title ?? "",
                          email: c.email,
                          phone: c.phone ?? "",
                          notes: c.notes ?? "",
                        });
                        setContactOpen(true);
                      }}>Modifier</Button>
                      <Button variant="ghost" size="sm" onClick={() => { if (confirm("Supprimer ce contact ?")) delContact.mutate(c.id); }}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <div>
        <h3 className="font-medium mb-2 flex items-center gap-2"><Tags className="h-4 w-4" />Catégories</h3>
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="text-right">Contacts</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {categories.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">Aucune catégorie</TableCell></TableRow>
              )}
              {categories.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{c.description ?? "—"}</TableCell>
                  <TableCell className="text-right"><Badge variant="secondary">{counts[c.id] ?? 0}</Badge></TableCell>
                  <TableCell className="text-right">
                    {canWrite && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => { setEditingCat({ id: c.id, name: c.name, description: c.description ?? "" }); setCatOpen(true); }}>
                          Modifier
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { if (confirm(`Supprimer la catégorie "${c.name}" ? Les contacts seront conservés sans catégorie.`)) delCat.mutate(c.id); }}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing.id ? "Modifier le contact" : "Nouveau contact"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Prénom</Label><Input value={editing.first_name} onChange={(e) => setEditing({ ...editing, first_name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Nom</Label><Input value={editing.last_name} onChange={(e) => setEditing({ ...editing, last_name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Organisme</Label><Input value={editing.organization} onChange={(e) => setEditing({ ...editing, organization: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Fonction</Label><Input placeholder="Président, secrétaire…" value={editing.job_title} onChange={(e) => setEditing({ ...editing, job_title: e.target.value })} /></div>
            <div className="space-y-1.5 col-span-2"><Label>Email *</Label><Input type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Téléphone</Label><Input value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Catégorie</Label>
              <select
                className="w-full border rounded-md h-9 px-3 bg-background text-sm"
                value={editing.category_id}
                onChange={(e) => setEditing({ ...editing, category_id: e.target.value })}
              >
                <option value="">— Aucune —</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5 col-span-2"><Label>Notes</Label><Textarea value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContactOpen(false)}>Annuler</Button>
            <Button onClick={() => saveContact.mutate()} disabled={!editing.email || saveContact.isPending}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingCat.id ? "Modifier la catégorie" : "Nouvelle catégorie"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Nom *</Label><Input value={editingCat.name} onChange={(e) => setEditingCat({ ...editingCat, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea value={editingCat.description} onChange={(e) => setEditingCat({ ...editingCat, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatOpen(false)}>Annuler</Button>
            <Button onClick={() => saveCat.mutate()} disabled={!editingCat.name || saveCat.isPending}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
